import { Bell, ChevronDown, LayoutGrid, LogOut, Plug, Users } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { NavLink } from "react-router-dom";
import { signOut } from "../lib/auth";
import type { Me } from "../lib/api";
import { cx } from "./ui";

export function Wordmark({ className = "h-6" }: { className?: string }) {
  return (
    <>
      <img src="/brand/intellias-logo.png" alt="Intellias" className={cx("wordmark-light w-auto", className)} />
      <img src="/brand/intellias-logo-white.png" alt="Intellias" className={cx("wordmark-dark w-auto", className)} />
    </>
  );
}

/**
 * Portal frame (design §7): Intellias header, tenant name (the logo slot comes with tenant branding), connection
 * picker, notifications bell and profile menu; "Powered by Intellias" is always shown.
 */
export function Shell({ me, children }: { me: Me; children: ReactNode }) {
  const tenantSide = me.kind === "tenant";
  const nav = [
    tenantSide && { to: "/", label: "Apps", icon: LayoutGrid, end: true },
    tenantSide && me.tenantAdmin && { to: "/admin/users", label: "Users", icon: Users },
    !tenantSide && { to: "/", label: "Tenants", icon: LayoutGrid, end: true },
  ].filter(Boolean) as { to: string; label: string; icon: typeof Users; end?: boolean }[];

  return (
    <div className="min-h-full flex flex-col">
      <div className="brand-bar h-1" aria-hidden />
      <header className="bg-surface border-b border-line">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center gap-4">
          <Wordmark />
          <span className="text-line" aria-hidden>
            |
          </span>
          <span className="font-display font-semibold text-ink truncate">{tenantSide ? me.tenant!.name : "Platform console"}</span>
          <nav className="hidden sm:flex items-center gap-1 ml-4" aria-label="Main">
            {nav.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => cx("inline-flex items-center gap-1.5 px-3 h-8 rounded-lg text-sm", isActive ? "bg-accent-soft text-accent-text font-medium" : "text-ink-2 hover:bg-surface-2")}>
                <n.icon className="size-4" aria-hidden /> {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2">
            {tenantSide && (
              <button type="button" disabled title="Infor connections arrive in phase 1, step 3" className="hidden md:inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-line text-sm text-muted">
                <Plug className="size-4" aria-hidden /> No connection
              </button>
            )}
            <button type="button" aria-label="Notifications" className="size-9 inline-flex items-center justify-center rounded-lg text-ink-2 hover:bg-surface-2">
              <Bell className="size-4" aria-hidden />
            </button>
            <ProfileMenu me={me} />
          </div>
        </div>
      </header>
      <main className="flex-1 max-w-6xl w-full mx-auto px-4 py-6">{children}</main>
      <footer className="border-t border-line text-xs text-muted">
        <div className="max-w-6xl mx-auto px-4 h-12 flex items-center">Powered by Intellias</div>
      </footer>
    </div>
  );
}

function ProfileMenu({ me }: { me: Me }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const initials = (me.name || me.email).split(/[\s@.]+/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("");
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="inline-flex items-center gap-1 h-9 pl-1 pr-2 rounded-lg hover:bg-surface-2">
        <span className="size-7 rounded-full bg-accent-chip text-accent-text text-xs font-semibold inline-flex items-center justify-center">{initials}</span>
        <ChevronDown className="size-4 text-muted" aria-hidden />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-64 rounded-xl border border-line bg-surface shadow-lg p-2 z-20">
          <div className="px-2 py-1.5">
            <div className="text-sm font-medium text-ink truncate">{me.name || me.email}</div>
            <div className="text-xs text-muted truncate">{me.email}</div>
          </div>
          <button type="button" onClick={() => void signOut()} className="w-full flex items-center gap-2 px-2 h-9 rounded-lg text-sm text-ink-2 hover:bg-surface-2">
            <LogOut className="size-4" aria-hidden /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}
